import type { MenuProps } from 'src/antd'

export type AntdMenuItem = NonNullable<MenuProps['items']>[number]

export interface MenuItemWithLink {
  key: string
  link?: string
  children?: MenuItemWithLink[]
}

export interface MenuItemWithSkipLinks {
  key?: string
  link?: string
  skipLinks?: string[]
}

/**
 * Resolve which sidebar nav key should be highlighted for the current path.
 *
 * Matching is on the ROUTE (a candidate's `link`, or a `skipLink` a collapsed
 * group owns) and is segment-aware: a candidate is active only when the path
 * IS its link or sits directly beneath it (`${link}/…`). Earlier this also
 * tested the bare nav `key` with `path.includes(target)`, so an unrelated route
 * that merely contained a key as a substring — the settings page
 * `/app/settings/company/:id/invoicing` vs the module key `invoicing` — kept the
 * module entry wrongly highlighted (PRO-171). When several candidates match
 * (a collapsed group and one of its sub-paths), the longest match wins.
 */
export const findBestMatchKey = (
  path: string,
  items: AntdMenuItem[]
): string | undefined => {
  const candidates: { key: string; link: string }[] = []

  const addCandidate = (key: string, link: string) =>
    candidates.push({ key, link })

  const processItem = (item: unknown) => {
    if (!item || typeof item !== 'object' || !('key' in item) || !item.key)
      return

    const menuItem = item as MenuItemWithLink
    const key = String(menuItem.key)
    const link = typeof menuItem.link === 'string' ? menuItem.link : key
    addCandidate(key, link)

    // A top-level direct-link item (a collapsed former group like Tickets or
    // Fleet) owns sub-paths via its own skipLinks — same contract as child
    // skipLinks below, so it stays highlighted on e.g. /app/tickets/kanban.
    const itemWithSkipLinks = item as MenuItemWithSkipLinks
    if (Array.isArray(itemWithSkipLinks.skipLinks)) {
      itemWithSkipLinks.skipLinks.forEach((skipLink: string) =>
        addCandidate(key, skipLink)
      )
    }

    if (Array.isArray(menuItem.children)) {
      menuItem.children.forEach(processItem)
      menuItem.children.forEach((child) => {
        const childWithLinks = child as MenuItemWithSkipLinks
        // A child's link/skipLinks belong to the CHILD's key, not the
        // parent group — so a collapsed verb that owns second-level
        // sub-paths (skipLinks) highlights itself, not just its section
        // header, while the user is on one of them.
        const childKey =
          typeof childWithLinks.key === 'string' ? childWithLinks.key : key
        if (childWithLinks.link) {
          addCandidate(childKey, childWithLinks.link)
        }
        if (Array.isArray(childWithLinks.skipLinks)) {
          childWithLinks.skipLinks.forEach((skipLink: string) =>
            addCandidate(childKey, skipLink)
          )
        }
      })
    }
  }

  items.forEach(processItem)

  // Segment-aware: exact route, or a sub-path under `${link}/`. Never a bare
  // substring match (the PRO-171 false-positive), and never a sibling that
  // only shares a prefix (`/app/invoicing` must not match
  // `/app/invoicing-templates`).
  const isActiveRoute = (target: string) =>
    path === target || path.startsWith(`${target}/`)

  const matches = candidates.filter(({ link }) => isActiveRoute(link))

  return matches.sort(
    (a, b) =>
      Math.max(b.key.length, b.link.length) -
      Math.max(a.key.length, a.link.length)
  )[0]?.key
}
